<script setup>
import { ref, onMounted, computed, defineProps, defineEmits } from 'vue';
import { CircleCheckIcon } from 'vue-tabler-icons';
import { useStore } from 'vuex';

const props = defineProps({
  resourceType: { type: String, required: true },
  resourceLabel: { type: String, required: true },
  isSelectable: { type: Boolean, default: false },
  selected: { type: String, default: undefined },
});

const emit = defineEmits(['update:selected']);

const store = useStore();

const loading = ref(true);

const filters = computed(() => store.getters[`${props.resourceType}s/${props.resourceType}Filters`]);
const resources = computed(() => store.getters[`${props.resourceType}s/${props.resourceType}s`]);
const currentPage = computed(() => Math.floor(filters.value.offset / filters.value.limit) + 1);
const pages = computed(() => {
  if (!filters.value.total) {
    return [];
  }
  const total = Math.ceil(filters.value.total / filters.value.limit);
  const value = Array(total)
    .fill()
    .map((_, index) => index + 1)
    .filter((_, index) => index > currentPage.value - 4 && index < currentPage.value + 2);

  if (value[0] !== 1) {
    value.unshift('⋮');
    value.unshift(1);
  }
  if (value[value.length - 1] !== total) {
    value.push('⋮');
    value.push(total);
  }
  return value;
});

const filterMutation = `${props.resourceType}s/set${props.resourceType.replace(/\b\w/g, (c) =>
  c.toUpperCase(),
)}Filters`;
const fetchAction = `${props.resourceType}s/fetch${props.resourceType.replace(/\b\w/g, (c) => c.toUpperCase())}s`;

async function changePageByNumber(page = currentPage.value) {
  loading.value = true;
  store.commit(filterMutation, {
    offset: (page - 1) * filters.value.limit,
  });
  try {
    await store.dispatch(fetchAction);
  } finally {
    loading.value = false;
  }
}

onMounted(changePageByNumber);

const selectedResource = ref(props.selected);
</script>

<template>
  <section
    class="resource-listing relative flex h-full w-full flex-col pb-12"
    :class="{
      'pointer-events-none': loading,
      'opacity-60': loading,
    }"
  >
    <div
      class="row-span-1 grid h-full gap-6 overflow-y-auto bg-white p-6"
      :class="{ 'grid-cols-4': isSelectable, 'grid-cols-8': !isSelectable }"
    >
      <router-link
        v-for="resource in resources"
        :key="resource.shortId"
        v-slot="{ href, navigate }"
        :to="`/${resourceLabel}s/${resource.shortId}`"
        custom
      >
        <label
          v-if="isSelectable"
          class="resource-listing__resource grid cursor-pointer gap-y-4 rounded-2xl bg-offwhite px-2 py-2"
          style="grid-template-columns: 3rem auto"
          :href="href"
        >
          <div class="relative flex h-10 w-10 cursor-pointer items-center">
            <input
              v-model="selectedResource"
              type="radio"
              name="selected-resource"
              class="absolute h-0 w-0 opacity-0"
              :value="resource.shortId"
              @input="emit('update:selected', $event.target.value)"
            />
            <img
              :src="
                resource.files?.find(({ name }) => name === 'initial')?.url ||
                'https://placehold.jp/10/f3f4f6/2e2925/70x70.png'
              "
              style="aspect-ratio: 1 / 1"
              :alt="resource.name"
              class="h-full w-full"
              :class="{
                'opacity-70': resource.shortId === selectedResource,
                'rounded-2xl ': !isSelectable,
                'rounded-full border-2 border-gray-300': isSelectable,
              }"
            />
            <circle-check-icon
              v-if="resource.shortId === selectedResource"
              class="absolute left-0 h-full w-full text-brand-600"
            ></circle-check-icon>
          </div>

          <div class="flex flex-col px-4 pb-2 text-black">
            <aside class="resource-listing__resource__short-id -mb-1 text-xs text-gray-800 opacity-70">
              {{ resource.shortId }}
            </aside>
            <p class="truncate">{{ resource.name }}</p>
          </div>
        </label>

        <a
          v-else
          class="resource-listing__resource flex flex-col gap-y-4 rounded-2xl bg-offwhite px-2 py-2 hover:bg-blue-50"
          :href="href"
          style="aspect-ratio: 2 / 3"
          @click="navigate"
        >
          <img
            :src="
              resource.files?.find(({ name }) => name === 'initial')?.url ||
              'https://placehold.jp/12/ffffff/004A7C/120x120.png'
            "
            :alt="resource.name"
            class="w-full rounded-2xl bg-gray-100"
            style="aspect-ratio: 1 / 1"
          />

          <div class="flex flex-grow flex-col px-4 pb-2 text-black">
            <aside class="resource-listing__resource__short-id -mb-1 text-xs text-gray-800 opacity-70">
              {{ resource.shortId }}
            </aside>
            <p class="truncate">{{ resource.name }}</p>
            <span class="text-sm text-brand-600 opacity-90">
              <small>Latest version</small> {{ resource.version }}
            </span>
            <slot class="" name="actions" :resource="resource"></slot>
          </div>
        </a>
      </router-link>
    </div>

    <footer class="absolute bottom-0 flex w-full items-center justify-center gap-4 bg-offwhite py-2 shadow">
      <p class="resource-list__page-total py-2 text-sm text-black">{{ filters.total }} items</p>

      <div class="flex justify-center text-black">
        <div
          class="resource-list__page-previous"
          :class="{
            'pointer-events-none opacity-70': currentPage === 1,
          }"
          @click="changePageByNumber(currentPage - 1)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="feather feather-chevron-left h-6 w-6"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </div>
        <div class="flex h-8 rounded bg-white font-medium">
          <div
            v-for="page in pages"
            :id="`resource-list__page-${page}`"
            :key="page"
            class="resource-list__page"
            :class="{
              'pointer-events-none': page === '...',
              'resource-list__page--active': page === currentPage,
            }"
            @click="changePageByNumber(page)"
          >
            {{ page }}
          </div>
        </div>
        <div
          class="resource-list__page-next"
          :class="{
            'pointer-events-none opacity-70': currentPage === pages[pages.length - 1],
          }"
          @click="changePageByNumber(currentPage + 1)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="feather feather-chevron-right h-6 w-6"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </div>
      </div>
    </footer>
  </section>
</template>

<style lang="postcss" scoped>
.resource-list__page {
  @apply flex w-8 cursor-pointer items-center justify-center rounded;
}
.resource-list__page:hover,
.resource-list__page-previous:hover,
.resource-list__page-next:hover {
  @apply bg-brand-100;
}
.resource-list__page--active {
  @apply pointer-events-none bg-brand-500 text-white;
}
.resource-list__page-previous,
.resource-list__page-next {
  @apply mr-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded bg-white;
}
</style>
